import { Calendar, ChevronRight, Globe, Lock, Mail, Menu, Rss, Search, TrendingUp, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { NewsletterModal } from "../components/NewsletterModal";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../services/auth";
import type { Category } from "../types";

export function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: categories } = useApi<Category[]>("/api/navigation", []);

  const todayStr = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(new Date());

  function submitSearch(event: React.FormEvent) {
    event.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
    }
  }

  // Close mobile nav on route change
  useEffect(() => {
    setOpen(false);
  }, [navigate]);

  return (
    <div className="site-shell magazine-theme">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      {/* Top Editorial Utility Strip */}
      <div className="mag-top-strip">
        <div className="mag-container mag-top-inner">
          <div className="mag-date-meta">
            <span className="mag-meta-item">
              <Calendar size={13} className="text-amber-600" />
              <span>{todayStr}</span>
            </span>
            <span className="mag-meta-divider">•</span>
            <span className="mag-meta-item">
              <Globe size={13} className="text-emerald-600" />
              <span>Harare 24°C • Bulawayo 22°C</span>
            </span>
            <span className="mag-meta-divider">•</span>
            <span className="mag-edition-tag">
              <span className="edition-accent-dot" />
              ZIMBABWE DIGITAL EDITION
            </span>
          </div>

          <div className="mag-top-actions">
            <div className="mag-trending-ticker">
              <span className="ticker-label">
                <span className="ticker-pulse-ruby" />
                <TrendingUp size={12} />
                <span>TRENDING:</span>
              </span>
              <Link to="/technology" className="ticker-item">Fintech Interoperability</Link>
              <span className="ticker-dot">•</span>
              <Link to="/innovation" className="ticker-item">Commercial Solar</Link>
              <span className="ticker-dot">•</span>
              <Link to="/reviews" className="ticker-item">5G Speed Test</Link>
            </div>

            <div className="mag-auth-links">
              {user ? (
                <Link to="/admin" className="mag-admin-badge" title="Go to Newsroom CMS">
                  <Lock size={12} />
                  <span>Newsroom</span>
                </Link>
              ) : (
                <Link to="/admin/login" className="mag-login-link" title="Staff Login">
                  <span>Staff Sign In</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Magazine Masthead */}
      <header className="site-header mag-header">
        <div className="mag-color-ribbon" />
        <div className="mag-container mag-masthead-wrap">
          <div className="mag-masthead-left">
            <button
              className="mag-menu-toggle mobile-only"
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu size={22} />
            </button>
            <div className="mag-issue-badge desktop-only">
              <span className="vol-text">VOL. IV • NO. 32</span>
              <span className="freq-text">DAILY EDITORIAL</span>
            </div>
          </div>

          <div className="mag-masthead-center">
            <Link to="/" className="mag-brand" aria-label="ZimSmart Magazine Home">
              <img className="mag-brand-image" src="/brand/zimsmart-logo.png" alt="ZimSmart" />
              <span className="mag-brand-sub">THE JOURNAL OF TECHNOLOGY, BUSINESS & MODERN LIVING</span>
            </Link>
          </div>

          <div className="mag-masthead-right">
            <button
              type="button"
              className="mag-search-btn"
              onClick={() => setSearchOpen((prev) => !prev)}
              aria-label="Toggle Search"
              title="Search ZimSmart"
            >
              <Search size={18} />
              <span className="desktop-only">Search</span>
            </button>
            <Link to="/newsletter" className="mag-subscribe-pill desktop-only">
              <Mail size={14} />
              <span>Daily Briefing</span>
            </Link>
          </div>
        </div>

        {/* Search Bar Flyout */}
        {searchOpen && (
          <div className="mag-search-flyout">
            <div className="mag-container">
              <form className="mag-search-form" onSubmit={submitSearch}>
                <Search size={20} className="mag-search-icon" />
                <input
                  id="header-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles, market reports, analysis, tech reviews..."
                  autoFocus
                />
                <button type="submit" className="mag-search-submit">Search</button>
                <button
                  type="button"
                  className="mag-search-close"
                  onClick={() => setSearchOpen(false)}
                  aria-label="Close search"
                >
                  <X size={18} />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Primary Magazine Navigation Bar */}
        <div className="mag-nav-container">
          <nav className={`mag-nav ${open ? "is-open" : ""}`} aria-label="Magazine categories">
            <div className="mag-nav-header mobile-only">
              <span className="mag-nav-title">ZIMSMART SECTIONS</span>
              <button
                className="icon-button nav-close"
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>

            <NavLink to="/" end className={({ isActive }) => (isActive ? "nav-link active nav-cat-cover" : "nav-link nav-cat-cover")} onClick={() => setOpen(false)}>
              Cover
            </NavLink>
            {categories?.filter((category) => !["how-to", "guides", "reviews"].includes(category.slug.toLowerCase())).map((category) => (
              <NavLink
                key={category.id}
                to={`/${category.slug}`}
                className={({ isActive }) => (isActive ? `nav-link active nav-cat-${category.slug}` : `nav-link nav-cat-${category.slug}`)}
                onClick={() => setOpen(false)}
              >
                {category.name}
              </NavLink>
            ))}
            <NavLink to="/how-to" className={({ isActive }) => (isActive ? "nav-link active nav-cat-how-to" : "nav-link nav-cat-how-to")} onClick={() => setOpen(false)}>
              Guides
            </NavLink>
            <NavLink to="/reviews" className={({ isActive }) => (isActive ? "nav-link active nav-cat-reviews" : "nav-link nav-cat-reviews")} onClick={() => setOpen(false)}>
              Reviews
            </NavLink>
            <NavLink to="/newsletter" className="nav-link nav-highlight" onClick={() => setOpen(false)}>
              <Mail size={14} className="inline mr-1 text-amber-500" />
              <span>Newsletter</span>
            </NavLink>
          </nav>
        </div>
      </header>

      {/* Main Content Viewport */}
      <main id="main" className="mag-main">
        <Outlet />
      </main>

      {/* Prestige Editorial Magazine Footer */}
      <footer className="mag-footer">
        <div className="mag-container">
          {/* Footer Top: Brand & Newsletter */}
          <div className="mag-footer-top">
            <div className="mag-footer-brand-col">
              <div className="mag-footer-logo">
                <img className="mag-footer-brand-image" src="/brand/zimsmart-logo.png" alt="ZimSmart" />
                <span className="mag-logo-sub">JOURNAL OF PROGRESS & COMMERCE</span>
              </div>
              <p className="mag-footer-desc">
                ZimSmart is Zimbabwe's independent digital magazine reporting on technology, capital markets, renewable infrastructure, and modern culture across Southern Africa.
              </p>
              <div className="mag-footer-meta-pill">
                <span>HARARE NEWSROOM</span>
                <span className="dot">•</span>
                <span>INDEPENDENT JOURNALISM</span>
              </div>
            </div>

            <div className="mag-footer-newsletter-col">
              <h3 className="mag-footer-heading">THE MORNING DISPATCH</h3>
              <p className="mag-footer-subtext">
                Every weekday morning, get our curated intelligence brief on market movements, tech developments, and executive interviews.
              </p>
              <Link to="/newsletter" className="mag-footer-cta-btn">
                <span>Join 24,000+ Readers</span>
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          {/* Footer Navigation Columns */}
          <div className="mag-footer-grid">
            <div className="mag-footer-col">
              <h4 className="mag-col-title">Editorial Sections</h4>
              <ul className="mag-col-links">
                <li><Link to="/technology">Technology & AI</Link></li>
                <li><Link to="/business">Business & Enterprise</Link></li>
                <li><Link to="/money">Money & Fintech</Link></li>
                <li><Link to="/innovation">Innovation & Energy</Link></li>
                <li><Link to="/digital-life">Digital Life</Link></li>
                <li><Link to="/reviews">Hardware Reviews</Link></li>
              </ul>
            </div>

            <div className="mag-footer-col">
              <h4 className="mag-col-title">Special Coverage</h4>
              <ul className="mag-col-links">
                <li><Link to="/how-to">Step-by-Step Guides</Link></li>
                <li><Link to="/tag/fintech">Fintech Deep Dives</Link></li>
                <li><Link to="/tag/solar">Solar Energy Grid</Link></li>
                <li><Link to="/tag/startups">Founder Spotlights</Link></li>
                <li><Link to="/newsletter">The Weekly Digest</Link></li>
              </ul>
            </div>

            <div className="mag-footer-col">
              <h4 className="mag-col-title">The Publication</h4>
              <ul className="mag-col-links">
                <li><Link to="/about">About ZimSmart</Link></li>
                <li><Link to="/contact">Editorial Newsroom</Link></li>
                <li><Link to="/advertise">Commercial Partnerships</Link></li>
                <li><a href="/rss.xml" target="_blank" rel="noreferrer">RSS Syndication</a></li>
                <li><Link to="/admin/login">Staff Portal</Link></li>
              </ul>
            </div>

            <div className="mag-footer-col">
              <h4 className="mag-col-title">Legal & Standards</h4>
              <ul className="mag-col-links">
                <li><Link to="/privacy">Privacy Notice</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><Link to="/cookie-policy">Cookie Policy</Link></li>
                <li><span className="mag-syndication-note">Content protected under Zimbabwean & international copyright laws.</span></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom Bar */}
          <div className="mag-footer-bottom">
            <p className="mag-copyright">
              © {new Date().getFullYear()} ZimSmart Media Group (Pvt) Ltd. All rights reserved. Registered Digital Publisher.
            </p>
            <div className="mag-footer-bottom-links">
              <a href="/rss.xml" className="mag-rss-link">
                <Rss size={14} />
                <span>RSS Feed</span>
              </a>
              <span className="divider">|</span>
              <span>Harare • Bulawayo • Johannesburg • London</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Global Newsletter Subscription Modal (30s Activity or Exit Intent) */}
      <NewsletterModal />
    </div>
  );
}
